/**
 * The curriculum areas the change register can be filtered to.
 *
 * Wider than the diff's own categories: `outcomes` covers everything at once,
 * and a reader looking at learning outcomes wants the programme's separated
 * from the modules'. So three of these are derived from a change's path rather
 * than read from its category.
 *
 * @module components/portfolio/comparison/comparison-areas
 */

import type { AcademicChange } from "@/components/portfolio/comparison/diff-types";
import type { RevisionChangeCategory } from "@/server/governance-diff";

export type ComparisonArea =
  RevisionChangeCategory | "all" | "programme-outcomes" | "module-outcomes";

export const COMPARISON_AREA_LABELS: Record<ComparisonArea, string> = {
  all: "All curriculum areas",
  identity: "Programme identity",
  narrative: "Programme details",
  "programme-outcomes": "Programme learning outcomes",
  "module-outcomes": "Module learning outcomes",
  outcomes: "Other learning outcomes",
  modules: "Modules",
  "credits-effort": "Credits and effort",
  assessments: "Assessments",
  mappings: "Traceability mapping",
};

/**
 * Whether a change belongs to an area.
 *
 * @param change - One academic change from the diff.
 * @param area - The area a reader has asked for.
 * @returns True when the change should be listed under that area.
 */
export function matchesComparisonArea(change: AcademicChange, area: ComparisonArea): boolean {
  if (area === "all") {
    return true;
  }
  if (area === "programme-outcomes") {
    return change.path.startsWith("plos[");
  }
  if (area === "module-outcomes") {
    return change.path.includes(".mimlos[");
  }
  // The two areas above are carved out of this one, so what is left is every
  // outcome change that belongs to neither.
  if (area === "outcomes") {
    return (
      change.category === "outcomes" &&
      !matchesComparisonArea(change, "programme-outcomes") &&
      !matchesComparisonArea(change, "module-outcomes")
    );
  }
  return change.category === area;
}
